/** Daily review queue with the caps from the method (section 4 of soile-method.md). */
import { CardState } from './fsrs.ts';

export type LangCode = 'zh' | 'en' | 'tr';

/** New cards per day by language. */
export const NEW_CARDS_PER_DAY: Record<LangCode, number> = { zh: 20, en: 10, tr: 10 };
/** When more reviews than this are due, new cards are paused. */
export const NEW_CARDS_PAUSE_THRESHOLD = 250;

export interface QueueCard {
  id: string;
  lang: string;
  state: number;
  due: Date;
  suspended: boolean;
  created_at: Date;
}

export interface ReviewQueue<C extends QueueCard> {
  /** Cards to show, due reviews first (oldest first), then new cards. */
  cards: C[];
  dueCount: number;
  newCount: number;
  newPaused: boolean;
}

export function newCardCap(lang: string): number {
  return lang in NEW_CARDS_PER_DAY ? NEW_CARDS_PER_DAY[lang as LangCode] : NEW_CARDS_PER_DAY.en;
}

/**
 * @param newSeenToday number of new cards already introduced today, per language.
 */
export function buildReviewQueue<C extends QueueCard>(
  all: readonly C[],
  now: Date,
  newSeenToday: Readonly<Record<string, number>>,
): ReviewQueue<C> {
  const active = all.filter((c) => !c.suspended);
  const due = active
    .filter((c) => c.state !== CardState.New && c.due.getTime() <= now.getTime())
    .sort((a, b) => a.due.getTime() - b.due.getTime());

  const newPaused = due.length > NEW_CARDS_PAUSE_THRESHOLD;
  const fresh: C[] = [];
  if (!newPaused) {
    const takenByLang: Record<string, number> = {};
    const candidates = active
      .filter((c) => c.state === CardState.New)
      .sort((a, b) => a.created_at.getTime() - b.created_at.getTime());
    for (const card of candidates) {
      const taken = (takenByLang[card.lang] ?? 0) + (newSeenToday[card.lang] ?? 0);
      if (taken >= newCardCap(card.lang)) continue;
      takenByLang[card.lang] = (takenByLang[card.lang] ?? 0) + 1;
      fresh.push(card);
    }
  }
  return { cards: [...due, ...fresh], dueCount: due.length, newCount: fresh.length, newPaused };
}

/**
 * Words are studied per language: the Words tab, the review queue and the due counts show
 * only the active course's cards. `null` (no course yet) shows nothing.
 */
export function cardsForLang<C extends { lang: string }>(cards: readonly C[], lang: string | null): C[] {
  return lang ? cards.filter((c) => c.lang === lang) : [];
}

/** «5 минут»: a short burst of 10–15 cards. */
export const QUICK_SESSION_MAX = 15;
export const QUICK_SESSION_MIN = 10;

/**
 * Cards for a «5 минут» session: the regular queue first (due reviews, then capped new
 * cards); when that gives fewer than QUICK_SESSION_MIN, the most recently added cards top it
 * up so a quick session is never empty while the learner has words at all.
 */
export function buildQuickSession<C extends QueueCard>(
  all: readonly C[],
  now: Date,
  newSeenToday: Readonly<Record<string, number>>,
): C[] {
  const queue = buildReviewQueue(all, now, newSeenToday).cards.slice(0, QUICK_SESSION_MAX);
  if (queue.length >= QUICK_SESSION_MIN) return queue;
  const taken = new Set(queue.map((c) => c.id));
  const recent = all
    .filter((c) => !c.suspended && !taken.has(c.id))
    .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
    .slice(0, QUICK_SESSION_MIN - queue.length);
  return [...queue, ...recent];
}
